import { useEffect, useRef, useState } from 'react';

interface MidPageCTAProps {
  heading?: string;
  subheading?: string;
  ctaLabel?: string;
  variant?: 'dark' | 'gold';
}

export default function MidPageCTA({
  heading = 'Ready to Tint Your Vehicle or Property?',
  subheading = 'Get a free quote from Philadelphia\'s professional window tinting studio.',
  ctaLabel = 'Get Free Quote',
  variant = 'dark',
}: MidPageCTAProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  if (variant === 'gold') {
    return (
      <section
        className="relative py-16 md:py-20 px-6 md:px-12 lg:px-16 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #C9A063 0%, #A07840 100%)' }}
        ref={ref}
      >
        <div
          className={`max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#080808] leading-[1.1] mb-2">{heading}</h2>
            <p className="text-[#080808]/65 font-sans text-base">{subheading}</p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <a
              href="tel:2155949404"
              className="whitespace-nowrap flex items-center gap-2 border border-[#080808]/25 text-[#080808] text-sm font-sans font-medium px-6 py-3.5 rounded-full hover:bg-[#080808]/10 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-sm"></i>
              </div>
              (215) 594-9404
            </a>
            <a
              href="#estimate"
              className="whitespace-nowrap flex items-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-7 py-3.5 rounded-full hover:bg-[#C4FF33] transition-colors cursor-pointer"
            >
              {ctaLabel}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative py-14 px-6 md:px-12 lg:px-16 overflow-hidden"
      style={{ background: '#0d0d0d' }}
      ref={ref}
    >
      <div className="absolute inset-0 border-y border-white/5 pointer-events-none" />
      <div
        className={`max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8 relative z-10 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="flex items-center gap-5">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#AAFF00]/10 border border-[#AAFF00]/20 shrink-0">
            <i className="ri-window-line text-[#AAFF00] text-xl"></i>
          </div>
          <div>
            <h3 className="font-serif text-white text-xl md:text-2xl font-bold leading-tight">{heading}</h3>
            <p className="text-white/40 text-sm font-sans mt-1">{subheading}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <a
            href="tel:2155949404"
            className="whitespace-nowrap flex items-center gap-2 border border-white/10 text-white/60 text-sm font-sans px-5 py-3 rounded-full hover:border-white/25 hover:text-white transition-all cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-[#AAFF00] text-sm"></i>
            </div>
            Call Now
          </a>
          <a
            href="#estimate"
            className="whitespace-nowrap flex items-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-6 py-3 rounded-full hover:bg-[#C4FF33] transition-colors cursor-pointer"
          >
            {ctaLabel}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
